
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "./utils";
import { getPricingConstants } from "./media-owners";

interface CampaignDetailsProps {
  costPerDevicePerDay?: number;
  devices: number;
  duration: number;
  mediaOwner?: string;
  selectedLocations?: Array<{ name: string; devices: number; mediaOwner?: string }>;
}

/**
 * Truncates location names to a specific length
 */
const truncateLocationName = (name: string, maxLength: number = 60): string => {
  if (!name) return "";
  return name.length > maxLength ? `${name.substring(0, maxLength - 3)}...` : name;
};

const CampaignDetails = ({ 
  costPerDevicePerDay, 
  devices, 
  duration,
  mediaOwner = 'TableAds',
  selectedLocations = []
}: CampaignDetailsProps) => {
  const { language } = useLanguage();
  
  // Group locations by media owner and calculate costs
  const mediaOwnerCosts: Record<string, { count: number, cost: number }> = {};
  
  if (selectedLocations.length > 0) {
    selectedLocations.forEach(location => {
      const locationMediaOwner = location.mediaOwner || mediaOwner;
      if (!mediaOwnerCosts[locationMediaOwner]) {
        const { costPerDevicePerDay } = getPricingConstants(locationMediaOwner);
        mediaOwnerCosts[locationMediaOwner] = { 
          count: location.devices,
          cost: costPerDevicePerDay
        };
      } else {
        mediaOwnerCosts[locationMediaOwner].count += location.devices;
      }
    });
  } else if (costPerDevicePerDay) {
    // If no locations but we have a cost per device, use that
    mediaOwnerCosts[mediaOwner] = { 
      count: devices,
      cost: costPerDevicePerDay
    };
  } else {
    // Default fallback
    const { costPerDevicePerDay } = getPricingConstants(mediaOwner);
    mediaOwnerCosts[mediaOwner] = { 
      count: devices,
      cost: costPerDevicePerDay
    };
  }

  // Calculate total device count (might differ from props.devices due to selected locations)
  const totalDeviceCount = Object.values(mediaOwnerCosts).reduce((sum, { count }) => sum + count, 0);

  return (
    <div className="space-y-2">
      {Object.entries(mediaOwnerCosts).map(([owner, { cost, count }]) => (
        <div key={owner} className="space-y-1">
          <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
              {language === 'ar' ? `:تكلفة الشاشة في اليوم (${owner})` : `Cost per screen per day (${owner}):`}
            </span>
            <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
              {cost.toFixed(2)} SAR
            </span>
          </div>
          <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'} ml-4`}>
              {language === 'ar' ? `:عدد الشاشات` : `Number of screens:`}
            </span>
            <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
              {formatNumber(count)}
            </span>
          </div>
        </div>
      ))}

      <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''} mt-3`}>
        <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {language === 'ar' ? ':إجمالي الشاشات' : 'Total screens:'}
        </span>
        <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {formatNumber(totalDeviceCount)}
        </span>
      </div>

      <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {language === 'ar' ? ':المدة' : 'Duration:'}
        </span>
        <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {duration} {language === 'ar' ? 'يوم' : 'days'}
        </span>
      </div>
    </div>
  );
};

export default CampaignDetails;
